import {
  GenerationJob,
  type EstimateOutput,
  type GenerationKind,
  type GenerationStatus,
} from "@dramapie/shared";

/**
 * 生成流水线的纯逻辑（M3）。
 *
 * 只做「把契约里的数字翻译成界面」：状态文案、进度、事件合并。
 * 花钱的语义（预扣 / 结算 / 退回）全部以服务端返回的 job 为准，
 * 前端不自己算账，也不自己推演 job 状态。
 */

/** 已实测单价（契约 §2.5）：`doubao-seedream-pro` 1024x1792 ≈ 405 点/张。 */
export const IMAGE_POINTS = 405;

/** 媒体槽位 = `GenerationKind`。镜头卡上首帧 / 尾帧 / 视频三块媒体就是它。 */
export type MediaSlot = GenerationKind;

export const MEDIA_SLOT_LABEL: Record<MediaSlot, string> = {
  keyframe: "首帧",
  endframe: "尾帧",
  clip: "视频",
};

export const GENERATION_KIND_META: Record<
  GenerationKind,
  { label: string; action: string; unit: string }
> = {
  keyframe: { label: "首帧", action: "生成首帧", unit: "张" },
  endframe: { label: "尾帧", action: "生成尾帧", unit: "张" },
  clip: { label: "视频", action: "生成视频", unit: "条" },
};

export const GENERATION_STATUS_META: Record<
  GenerationStatus,
  { label: string; className: string }
> = {
  queued: { label: "排队中", className: "bg-surface-2 text-muted" },
  running: { label: "生成中", className: "bg-warn-soft text-warn" },
  partial: { label: "部分完成", className: "bg-warn-soft text-warn" },
  succeeded: { label: "已完成", className: "bg-ok-soft text-ok" },
  failed: { label: "失败", className: "bg-danger-soft text-danger" },
  cancelled: { label: "已取消", className: "bg-surface-2 text-muted" },
};

/** 还在跑（可取消）的状态。 */
export function isActiveJob(status: GenerationStatus): boolean {
  return status === "queued" || status === "running";
}

/** 终态：点数已结算 / 退回，可以安全地重取余额。 */
export function isTerminalJob(status: GenerationStatus): boolean {
  return !isActiveJob(status);
}

export interface JobProgress {
  /** 已成功 item 数 */
  done: number;
  failed: number;
  total: number;
  /** 0–100，按已成功 item 占比 */
  percent: number;
}

export function jobProgress(job: GenerationJob): JobProgress {
  const total = job.items.length;
  const done = job.items.filter((item) => item.status === "succeeded").length;
  const failed = job.items.filter((item) => item.status === "failed").length;
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  return { done, failed, total, percent };
}

/**
 * 新 job 在前（`createdAt` 倒序），同一 job 原位覆盖。
 * 稳定排序：`createdAt` 相同时保留插入顺序，避免 WS 事件把列表抖来抖去。
 */
export function upsertJob(jobs: readonly GenerationJob[], job: GenerationJob): GenerationJob[] {
  const index = jobs.findIndex((item) => item.id === job.id);
  const next =
    index === -1 ? [job, ...jobs] : jobs.map((item, itemIndex) => (itemIndex === index ? job : item));
  return [...next].sort((a, b) => b.createdAt - a.createdAt);
}

export function replaceJobs(
  jobs: readonly GenerationJob[],
  incoming: readonly GenerationJob[],
): GenerationJob[] {
  // 全量快照以服务端为准：先清空再按同一套排序灌入。
  return [...incoming].sort((a, b) => b.createdAt - a.createdAt);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * 从 `job.updated` 的 payload 里取 job。
 * 契约只写了「发 job.updated」，没冻结 payload 形状；这里兼容
 * `{ job }`、`{ generation }` 与整个 payload 就是 job 三种写法。
 */
export function jobFromEvent(payload: Record<string, unknown>): GenerationJob | null {
  const candidates: unknown[] = [payload.job, payload.generation, payload];
  for (const candidate of candidates) {
    const parsed = GenerationJob.safeParse(candidate);
    if (parsed.success) return parsed.data;
  }
  return null;
}

/** 事件只带 id 时（例如 `{ jobId }`），调用方需要回查单 job。 */
export function jobIdFromEvent(payload: Record<string, unknown>): string | null {
  if (typeof payload.jobId === "string" && payload.jobId.length > 0) return payload.jobId;
  if (typeof payload.id === "string" && payload.id.startsWith("job_")) return payload.id;
  return null;
}

/**
 * `media.ready` 的 kind 字段：优先认生成 kind，
 * 退而求其次按 MediaRef 的 image/video 猜槽位（image 默认首帧）。
 */
export function mediaSlotFromEventKind(value: unknown): MediaSlot | null {
  if (value === "keyframe" || value === "endframe" || value === "clip") return value;
  // 契约 §2.7：`kind` 是**镜头字段名**，尾帧是驼峰 `endFrame`。
  if (value === "endFrame") return "endframe";
  // 兜底：拿到 MediaRef.kind 时按 image/video 猜（image 默认首帧）。
  if (value === "image") return "keyframe";
  if (value === "video") return "clip";
  return null;
}

export type EstimateDefaults = NonNullable<EstimateOutput["defaults"]>;

/**
 * 确认层要显示「本次按什么规格算钱」。
 * 服务端没给 defaults 时返回空数组（不猜、不硬编码）。
 */
export function formatEstimateDefaults(defaults: EstimateDefaults | undefined): string[] {
  if (!defaults) return [];
  const parts: string[] = [];
  if (typeof defaults.seconds === "number" && Number.isFinite(defaults.seconds)) {
    parts.push(`${defaults.seconds} 秒`);
  }
  if (typeof defaults.resolution === "string" && defaults.resolution.length > 0) {
    parts.push(defaults.resolution);
  }
  if (typeof defaults.size === "string" && defaults.size.length > 0) {
    parts.push(defaults.size.replace("x", "×"));
  }
  return parts;
}

/** 预估弹层要记住「这次要生成什么」，确认时原样提交。 */
export interface PendingGeneration {
  kind: GenerationKind;
  shotIds: string[];
  estimate: EstimateOutput;
}

/**
 * 单价（用于确认层显示「405 点/张」）。
 * 用服务端预估反推，避免前端写死价格和服务端漂移；
 * 预估不能被镜数整除时返回 null，界面只显示总额。
 */
export function estimatedUnitPoints(
  pending: PendingGeneration,
): { points: number; unit: string } | null {
  const count = pending.shotIds.length;
  const total = pending.estimate.estimatedPoints;
  if (count === 0 || total <= 0 || total % count !== 0) return null;
  return { points: total / count, unit: GENERATION_KIND_META[pending.kind].unit };
}
